// "👋 Amy was added to the channel" goes out under the inviter's name, but
// isn't something they wrote: no receipt under it.
const isJoinNotice = (m) => /^👋 .* was added to the channel$/.test(String(m.body || ""));

const sortByReadTime = (readers) => readers.sort((a, b) => Date.parse(a.at) - Date.parse(b.at));

// "Seen by ..." / "Sent" under your own messages. readBy is every other
// member with their last_read_at ([{ user_id, name, read_at }]). Returns
// { [messageId]: receipt }, where receipt is { seen: false } or
// { seen: true, readers: [{ name, at }] }.
//
// In a channel (perMessage), every message lists everyone besides its
// sender who has read up to it. Your newest reads "Sent" while nobody has.
//
// In a DM, each reader appears once, under the newest of your messages
// they have read, unless they posted after it (a reply already says they
// read it). Your newest message reads "Sent" while nobody has read it, if
// nobody has posted since.
export function seenReceipts(messages, myId, readBy = [], { perMessage = false } = {}) {
  const ownIndexes = messages
    .map((m, i) => (m.sender_id === myId && !isJoinNotice(m) ? i : -1))
    .filter((i) => i >= 0);
  const receipts = {};

  if (perMessage) {
    const readersOf = (m) =>
      sortByReadTime(
        readBy
          .filter((r) => r.user_id !== m.sender_id && r.read_at && Date.parse(r.read_at) >= Date.parse(m.created_at))
          .map((r) => ({ name: r.name, at: r.read_at })),
      );
    for (const m of messages) {
      if (isJoinNotice(m) || m.sender_name === "System") continue;
      const readers = readersOf(m);
      if (readers.length) receipts[m.id] = { seen: true, readers };
    }
    if (ownIndexes.length) {
      const newestId = messages[ownIndexes[ownIndexes.length - 1]].id;
      if (!receipts[newestId]) receipts[newestId] = { seen: false };
    }
    return receipts;
  }

  if (!ownIndexes.length) return {};

  const lastPostIndex = {};
  messages.forEach((m, i) => {
    lastPostIndex[m.sender_id] = i;
  });
  for (const reader of readBy) {
    const readMs = reader.read_at ? Date.parse(reader.read_at) : NaN;
    if (!Number.isFinite(readMs)) continue;
    const idx = [...ownIndexes].reverse().find((i) => Date.parse(messages[i].created_at) <= readMs);
    if (idx === undefined || (lastPostIndex[reader.user_id] ?? -1) > idx) continue;
    const id = messages[idx].id;
    receipts[id] = receipts[id] || { seen: true, readers: [] };
    receipts[id].readers.push({ name: reader.name, at: reader.read_at });
  }
  Object.values(receipts).forEach((r) => sortByReadTime(r.readers));

  const newest = ownIndexes[ownIndexes.length - 1];
  if (!receipts[messages[newest].id] && newest === messages.length - 1) receipts[messages[newest].id] = { seen: false };
  return receipts;
}

// "2:14 PM" today, "Oct 3, 2:14 PM" before that.
export function readTime(iso, now = new Date()) {
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return "";
  const time = at.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
  if (at.toDateString() === now.toDateString()) return time;
  return `${at.toLocaleDateString([], { month: "short", day: "numeric" })}, ${time}`;
}

// "Seen by Amy · 2:14 PM" for one reader; "Seen by Amy, Bob, Cara" or
// "Seen by Amy, Bob +3" for more (their times are in receiptTitle).
export function receiptLabel(receipt, now = new Date()) {
  if (!receipt.seen) return "Sent";
  const { readers } = receipt;
  if (readers.length === 1) {
    const time = readTime(readers[0].at, now);
    return `Seen by ${readers[0].name}${time ? ` · ${time}` : ""}`;
  }
  const names = readers.map((r) => r.name);
  if (names.length <= 3) return `Seen by ${names.join(", ")}`;
  return `Seen by ${names.slice(0, 2).join(", ")} +${names.length - 2}`;
}

// Hover text: who read it and when, one per line.
export function receiptTitle(receipt, now = new Date()) {
  if (!receipt.seen) return "Not read yet";
  return receipt.readers.map((r) => `${r.name} — ${readTime(r.at, now)}`).join("\n");
}

// The later of two ISO timestamps (a stale "read" event must not move it back).
export function laterReadAt(current, next) {
  if (!next) return current;
  if (!current) return next;
  return Date.parse(next) > Date.parse(current) ? next : current;
}

// readBy with one reader's newer read folded in (added if not listed yet).
export function mergeReadBy(readBy, reader) {
  if (!reader?.user_id) return readBy;
  const existing = readBy.find((r) => r.user_id === reader.user_id);
  if (!existing) return [...readBy, { user_id: reader.user_id, name: reader.name || "Unknown", read_at: reader.read_at || null }];
  const readAt = laterReadAt(existing.read_at, reader.read_at);
  if (readAt === existing.read_at) return readBy;
  return readBy.map((r) => (r.user_id === reader.user_id ? { ...r, read_at: readAt } : r));
}
